/**
 * Two-tier cache: memory (process lifetime) + localStorage (cross-session).
 *
 * Usage pattern — stale-while-revalidate:
 *   1. Call getStale(key) synchronously → returns whatever's in cache (may be old)
 *   2. Set state immediately so the UI renders without a spinner
 *   3. Call getCached(key, fetchFn) which returns fresh data when ready
 *   4. Update state with fresh data (usually identical → React bails out cheaply)
 *
 * After any write operation call bust(prefix) so the next read goes to the server.
 */

const CACHE = new Map();           // memory cache — lives for the browser tab session
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const LS_PREFIX   = 'epc_cache_';  // localStorage key namespace

// ── localStorage helpers (all wrapped — localStorage can throw in private mode) ──

function lsGet(key) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    return parsed; // { data, ts }
  } catch { return null; }
}

function lsSet(key, data) {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* quota exceeded or private mode — silently skip persistence */ }
}

function lsDel(prefix) {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX + prefix)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Synchronously return whatever data is cached (memory first, then localStorage).
 * Returns null when nothing is cached at all.
 *
 * Does NOT enforce TTL — intentionally returns stale data so callers can display
 * something immediately while a background refresh is in flight.
 */
export function getStale(key) {
  const mem = CACHE.get(key);
  if (mem) return mem.data;

  const ls = lsGet(key);
  if (ls) {
    CACHE.set(key, ls); // warm the memory cache for the rest of this session
    return ls.data;
  }
  return null;
}

/**
 * Return fresh data, using the cache when it is still within TTL.
 *
 * Flow:
 *  1. Memory cache hit → resolve immediately (no I/O at all)
 *  2. localStorage hit (within TTL) → resolve immediately, memory cache warmed
 *  3. Both expired / missing → call fetchFn, store result in both caches
 */
export function getCached(key, fetchFn, ttl = DEFAULT_TTL) {
  const mem = CACHE.get(key);
  if (mem && Date.now() - mem.ts < ttl) return Promise.resolve(mem.data);

  const ls = lsGet(key);
  if (ls && Date.now() - ls.ts < ttl) {
    CACHE.set(key, ls);
    return Promise.resolve(ls.data);
  }

  return Promise.resolve(fetchFn()).then((data) => {
    const entry = { data, ts: Date.now() };
    CACHE.set(key, entry);
    lsSet(key, data); // persist for cross-session stale-first rendering
    return data;
  });
}

/**
 * Fire-and-forget prefetch.
 * Skips silently if data is already in the memory cache (avoids duplicate fetches).
 * Safe to call multiple times — only one real network request per TTL window.
 */
export function prefetch(key, fetchFn) {
  if (CACHE.has(key)) return; // already warm — nothing to do
  getCached(key, fetchFn).catch(() => { /* prefetch failures are non-fatal */ });
}

/**
 * Invalidate all cache entries whose key starts with prefix (both caches).
 * Call after any write so the next read fetches fresh data from the server.
 *
 * @example bust('materials:')  // busts "materials:all", "materials:filtered", …
 */
export function bust(prefix) {
  for (const k of CACHE.keys()) if (k.startsWith(prefix)) CACHE.delete(k);
  lsDel(prefix);
}

/** Wipe the entire cache — both memory and localStorage (use on logout). */
export function bustAll() {
  CACHE.clear();
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}
